import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BLOG_CATEGORY_LABELS,
  fetchBlogPosts,
  formatBlogDate,
  getBlogCategoryLabel,
  getBlogPostDisplayDate,
} from '../lib/blogApi';

const categoryClass = (category) => `ff-category-badge ff-category-badge--${category || 'default'}`;

// Categories created after the label list was written still read as words, e.g. "points-guides".
export function formatCategoryLabel(category) {
  if (category && !BLOG_CATEGORY_LABELS[category] && /^[a-z0-9-]+$/.test(category)) {
    return category
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return getBlogCategoryLabel(category);
}

// Shared post card used by the home page and the posts index.
export const PostCard = ({ post, headingLevel = 3 }) => {
  const Heading = `h${headingLevel}`;
  const date = formatBlogDate(getBlogPostDisplayDate(post));
  const meta = [post.authorName, date].filter(Boolean).join(' • ');

  return (
    <Link to={`/posts/${post.slug}`} className="ff-post-card">
      <div className="ff-post-card__badge-row">
        <span className={categoryClass(post.category)}>{formatCategoryLabel(post.category)}</span>
      </div>

      {post.featuredImageUrl ? (
        <img
          className="ff-post-card__image"
          src={post.featuredImageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          width="640"
          height="360"
        />
      ) : (
        <div className="ff-post-card__image" aria-hidden="true" />
      )}

      <div className="ff-post-card__body">
        <Heading className="ff-post-card__title">{post.title}</Heading>
        {meta ? <div className="ff-post-card__meta">{meta}</div> : null}
        {post.excerpt ? <div className="ff-post-card__excerpt">{post.excerpt}</div> : null}
      </div>
    </Link>
  );
};

// Placeholder cards keep the layout steady while posts load.
export const PostCardSkeletons = ({ count = 2, label = 'Loading posts…' }) => (
  <div className="ff-panel-grid ff-panel-grid--2" role="status" aria-live="polite">
    <span className="ff-visually-hidden">{label}</span>
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="ff-post-card ff-post-card--skeleton" aria-hidden="true">
        <div className="ff-post-card__image" />
        <div className="ff-post-card__body">
          <div className="ff-skeleton-line ff-skeleton-line--title" />
          <div className="ff-skeleton-line ff-skeleton-line--short" />
        </div>
      </div>
    ))}
  </div>
);

export const PostsLoadError = ({ message, onRetry }) => (
  <div className="ff-notice ff-notice--error ff-posts-error" role="alert">
    <p className="ff-posts-error__message">{message}</p>
    <button type="button" className="ff-button-ghost ff-posts-error__retry" onClick={onRetry}>
      Try again
    </button>
  </div>
);

const Posts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const loadPosts = async () => {
      setLoading(true);
      setError('');
      try {
        const allPosts = await fetchBlogPosts({ limit: 4 }, { signal: controller.signal });
        if (!cancelled) {
          setPosts(allPosts.slice(0, 4));
        }
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          setError("We couldn't load the latest posts right now.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPosts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [attempt]);

  if (loading) {
    return <PostCardSkeletons count={2} label="Loading the latest posts…" />;
  }

  if (error) {
    return <PostsLoadError message={error} onRetry={() => setAttempt((value) => value + 1)} />;
  }

  if (posts.length === 0) {
    return <div className="ff-empty">No posts yet. Check back soon.</div>;
  }

  return (
    <>
      <div className="ff-panel-grid ff-panel-grid--2">
        {posts.map((post) => (
          <PostCard key={post.id} post={post} />
        ))}
      </div>
      <div className="ff-posts-more">
        <Link to="/posts" className="ff-button">See all posts</Link>
      </div>
    </>
  );
};

export default Posts;
